import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, RefreshCw, ShieldCheck, Trash2, Archive, MailOpen } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/contact")({
  component: AdminContactPage,
  head: () => ({ meta: [{ title: "Contact messages — Platform Admin" }] }),
});

type Row = {
  id: string;
  name: string;
  email: string;
  company: string | null;
  phone: string | null;
  message: string;
  status: string;
  ip: string | null;
  referer: string | null;
  notify_email_sent: boolean;
  confirmation_email_sent: boolean;
  created_at: string;
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "read", label: "Read" },
  { key: "archived", label: "Archived" },
] as const;

function AdminContactPage() {
  const { isPlatformAdmin } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<(typeof FILTERS)[number]["key"]>("all");

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("contact_submissions")
      .select("id,name,email,company,phone,message,status,ip,referer,notify_email_sent,confirmation_email_sent,created_at")
      .order("created_at", { ascending: false })
      .limit(300);
    if (error) toast.error(error.message);
    setRows((data ?? []) as Row[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isPlatformAdmin) void load();
    else setLoading(false);
  }, [isPlatformAdmin, load]);

  const setStatus = async (id: string, status: string) => {
    const { error } = await supabase.from("contact_submissions").update({ status }).eq("id", id);
    if (error) return toast.error(error.message);
    setRows((p) => p.map((r) => (r.id === id ? { ...r, status } : r)));
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("contact_submissions").delete().eq("id", id);
    if (error) return toast.error(error.message);
    setRows((p) => p.filter((r) => r.id !== id));
    toast.success("Message deleted");
  };

  if (!isPlatformAdmin) {
    return (
      <Card className="p-6 text-sm text-muted-foreground">
        <ShieldCheck className="mb-2 h-5 w-5 text-primary" />
        Platform admin access required.
      </Card>
    );
  }

  const visible = filter === "all" ? rows : rows.filter((r) => r.status === filter);
  const newCount = rows.filter((r) => r.status === "new").length;

  return (
    <div className="min-w-0 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-wider text-primary">Platform Admin</p>
          <h1 className="font-display text-2xl font-bold">Contact messages</h1>
          <p className="text-sm text-muted-foreground">
            Every submission from the public contact form — {rows.length} total, {newCount} new.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <Button
            key={f.key}
            size="sm"
            variant={filter === f.key ? "default" : "outline"}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {loading ? (
        <Card className="p-6 text-sm text-muted-foreground">Loading messages…</Card>
      ) : visible.length === 0 ? (
        <Card className="p-6 text-sm text-muted-foreground">
          <Mail className="mb-2 h-5 w-5 text-primary" />
          No messages here yet.
        </Card>
      ) : (
        <div className="space-y-3">
          {visible.map((r) => (
            <Card key={r.id} className="min-w-0 p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="truncate font-semibold">{r.name}</span>
                    {r.status === "new" && <Badge>New</Badge>}
                    {r.status === "archived" && <Badge variant="secondary">Archived</Badge>}
                  </div>
                  <div className="mt-1 text-sm">
                    <a href={`mailto:${r.email}`} className="break-all text-primary hover:underline">
                      {r.email}
                    </a>
                    {r.company ? <span className="text-muted-foreground"> · {r.company}</span> : null}
                    {r.phone ? (
                      <span className="text-muted-foreground">
                        {" "}
                        · <a href={`tel:${r.phone}`} className="hover:underline">{r.phone}</a>
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {new Date(r.created_at).toLocaleString()}
                    {r.notify_email_sent ? " · emailed" : " · email failed"}
                  </div>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  {r.status !== "read" && (
                    <Button size="sm" variant="outline" onClick={() => void setStatus(r.id, "read")}>
                      <MailOpen className="mr-1.5 h-3.5 w-3.5" /> Read
                    </Button>
                  )}
                  {r.status !== "archived" && (
                    <Button size="sm" variant="outline" onClick={() => void setStatus(r.id, "archived")}>
                      <Archive className="mr-1.5 h-3.5 w-3.5" /> Archive
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => void remove(r.id)}>
                    <Trash2 className="h-3.5 w-3.5 text-destructive" />
                  </Button>
                </div>
              </div>
              <p className="mt-3 whitespace-pre-wrap break-words rounded-lg bg-muted/40 p-3 text-sm">{r.message}</p>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
